/**
 * useSendFlow — SSOT hook for the full send-transfer lifecycle.
 *
 * Owns all state and handlers previously scattered across HomeScreen:
 *   - Staged file management (add, remove)
 *   - Transfer execution (prepare → share → result)
 *   - Result state (shareUrl, shareHash, torrent metadata)
 *
 * HomeScreen consumes this hook and passes the result straight to ActionCard.
 */
import { useState } from 'react';
import { Alert } from 'react-native';
import { useTransferViewModel } from '../viewmodels';
import { usePrepareProgress } from '@share/ui';
import { useToast } from '@share/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SendFlowState {
  // File staging
  stagedFiles:    File[];
  addFiles:       (files: File[]) => void;
  removeFile:     (index: number) => void;
  clearFiles:     () => void;

  // Expiry
  selectedExpiry: string;
  onExpiryChange: (expiry: string) => void;

  // Transfer lifecycle
  isSending:      boolean;
  isPreparing:    boolean;
  prepareProgress: number | undefined;

  // Result
  shareUrl:       string | null;
  shareHash:      string | null;
  torrentName:    string | undefined;
  torrentFile:    Uint8Array | undefined;

  // Actions
  handleTransfer: () => Promise<void>;
  handleReset:    () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSendFlow(opts?: { onNeedFiles?: () => void }): SendFlowState {
  const { startShare } = useTransferViewModel();
  const { showToast } = useToast();
  const {
    progress: prepareProgress,
    startSimulation,
    finishSimulation,
    resetProgress,
  } = usePrepareProgress();

  // ── File staging ────────────────────────────────────────────────────────────
  const [stagedFiles,    setStagedFiles]    = useState<File[]>([]);
  const [selectedExpiry, setSelectedExpiry] = useState<string>('3d');

  const addFiles  = (files: File[]) => setStagedFiles(prev => [...prev, ...files]);
  const removeFile = (index: number) =>
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  const clearFiles = () => setStagedFiles([]);

  // ── Transfer lifecycle ──────────────────────────────────────────────────────
  const [isSending,   setIsSending]   = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);

  // ── Result state ────────────────────────────────────────────────────────────
  const [shareUrl,     setShareUrl]     = useState<string | null>(null);
  const [shareHash,    setShareHash]    = useState<string | null>(null);
  const [torrentName,  setTorrentName]  = useState<string | undefined>(undefined);
  const [torrentFile,  setTorrentFile]  = useState<Uint8Array | undefined>(undefined);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleTransfer = async () => {
    // Guard: if no files staged, delegate to the file picker opener
    if (stagedFiles.length === 0) {
      opts?.onNeedFiles?.();
      return;
    }

    setIsSending(true);
    setIsPreparing(true);

    // Yield one frame so React renders the 0% state before heavy hashing work
    await new Promise<void>(r => setTimeout(r, 80));
    startSimulation();

    try {
      const { sessionId, magnetUri, torrentName, torrentFile } = await startShare(stagedFiles, { expiry: selectedExpiry });

      // Cubic ease-out from current progress → 100%, then 250ms linger
      await finishSimulation();

      setShareUrl(magnetUri);
      setShareHash(sessionId);
      setTorrentName(torrentName);
      setTorrentFile(torrentFile);
      clearFiles();

    } catch (e: any) {
      resetProgress();
      showToast({ type: 'error', message: e?.message ?? 'Transfer failed.' });
    } finally {
      setIsSending(false);
      setIsPreparing(false);
    }
  };

  const handleReset = () => {
    setShareUrl(null);
    setShareHash(null);
    setTorrentName(undefined);
    setTorrentFile(undefined);
    setIsPreparing(false);
    resetProgress();
  };

  return {
    stagedFiles, addFiles, removeFile, clearFiles,
    selectedExpiry, onExpiryChange: setSelectedExpiry,
    isSending, isPreparing, prepareProgress,
    shareUrl, shareHash, torrentName, torrentFile,
    handleTransfer, handleReset,
  };
}
